/**
 * Security Module - Saudi Matchmaker V3
 * الأمان + خوارزمية التوافق المحدثة
 */

import CryptoJS from 'crypto-js';

// ==================== Encryption ====================

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'default-key-change-in-production';

export const encryptData = (data) => {
  if (!data) return '';
  try {
    return CryptoJS.AES.encrypt(data.toString(), ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error('Encryption error:', error);
    return '';
  }
};

export const decryptData = (encryptedData) => {
  if (!encryptedData) return '';
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
};

// ==================== Input Validation ====================

export const sanitizeInput = (input) => {
  if (!input) return '';
  return input
    .toString()
    .trim()
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .substring(0, 1000);
};

export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

export const isValidSaudiPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/\D/g, '');
  // Saudi numbers: 05xxxxxxxx or 9665xxxxxxxx
  return /^(05\d{8}|9665\d{8})$/.test(cleaned);
};

export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('05')) {
    return '+966' + cleaned.substring(1);
  }
  if (cleaned.startsWith('9665')) {
    return '+' + cleaned;
  }
  return phone;
};

export const isValidAge = (dob) => {
  if (!dob) return false;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 18 && age <= 100;
};

// ==================== Rate Limiting ====================

class RateLimiter {
  constructor(maxAttempts, windowMs) {
    this.attempts = new Map();
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  check(key) {
    const now = Date.now();
    const record = this.attempts.get(key);

    if (!record) {
      this.attempts.set(key, { count: 1, firstAttempt: now });
      return { allowed: true };
    }

    if (now - record.firstAttempt > this.windowMs) {
      this.attempts.set(key, { count: 1, firstAttempt: now });
      return { allowed: true };
    }

    if (record.count >= this.maxAttempts) {
      const waitTime = Math.ceil((this.windowMs - (now - record.firstAttempt)) / 1000);
      return { allowed: false, waitTime };
    }

    record.count++;
    return { allowed: true };
  }

  reset(key) {
    this.attempts.delete(key);
  }
}

export const authRateLimiter = new RateLimiter(5, 300000); // 5 attempts per 5 minutes
export const messageRateLimiter = new RateLimiter(30, 60000); // 30 messages per minute

// ==================== Compatibility Algorithm ====================

export const checkFullCompatibility = (user1, user2) => {
  // 1. التحقق من الجنس (ذكر + أنثى فقط)
  if (!user1.gender || !user2.gender) {
    return { compatible: false, reason: 'missing_gender' };
  }
  
  if (user1.gender === user2.gender) {
    return { compatible: false, reason: 'same_gender' };
  }
  
  const validGenders = ['ذكر', 'أنثى'];
  if (!validGenders.includes(user1.gender) || !validGenders.includes(user2.gender)) {
    return { compatible: false, reason: 'invalid_gender' };
  }

  // 2. التحقق من القبول المتبادل للزواج السابق
  if (user1.previouslyMarried === 'نعم') {
    if (user2.prefMarried && user2.prefMarried.includes('لا')) {
      return { compatible: false, reason: 'partner_rejects_previously_married' };
    }
  }
  if (user2.previouslyMarried === 'نعم') {
    if (user1.prefMarried && user1.prefMarried.includes('لا')) {
      return { compatible: false, reason: 'user_rejects_previously_married' };
    }
  }

  // 3. التحقق من قبول الأطفال
  if (user1.hasChildren === 'نعم') {
    if (user2.prefChildren && user2.prefChildren.includes('لا')) {
      return { compatible: false, reason: 'partner_rejects_children' };
    }
  }
  if (user2.hasChildren === 'نعم') {
    if (user1.prefChildren && user1.prefChildren.includes('لا')) {
      return { compatible: false, reason: 'user_rejects_children' };
    }
  }

  // 4. التحقق من الاحتياجات الخاصة
  if (user1.disability && user1.disability !== 'لا يوجد' && user1.disability !== 'أفضل عدم الإجابة') {
    if (user2.prefDisability && user2.prefDisability.includes('لا أقبل')) {
      return { compatible: false, reason: 'partner_rejects_disability' };
    }
  }
  if (user2.disability && user2.disability !== 'لا يوجد' && user2.disability !== 'أفضل عدم الإجابة') {
    if (user1.prefDisability && user1.prefDisability.includes('لا أقبل')) {
      return { compatible: false, reason: 'user_rejects_disability' };
    }
  }

  // 5. التحقق من المنطقة (إقصائي)
  if (user1.prefRegion && !user1.prefRegion.includes('لا يهم')) {
    if (user2.region && !user1.prefRegion.includes(user2.region)) {
      return { compatible: false, reason: 'region_mismatch' };
    }
  }
  if (user2.prefRegion && !user2.prefRegion.includes('لا يهم')) {
    if (user1.region && !user2.prefRegion.includes(user1.region)) {
      return { compatible: false, reason: 'region_mismatch' };
    }
  }

  // 6. التحقق من أنهم لم ينسحبوا من بعض سابقاً
  if (user1.withdrawnFrom && user1.withdrawnFrom.includes(user2.id)) {
    return { compatible: false, reason: 'previously_withdrawn' };
  }
  if (user2.withdrawnFrom && user2.withdrawnFrom.includes(user1.id)) {
    return { compatible: false, reason: 'previously_withdrawn' };
  }

  // 7. التحقق من عدم وجود حظر
  if (user1.blockedUsers && user1.blockedUsers.includes(user2.id)) {
    return { compatible: false, reason: 'user_blocked' };
  }
  if (user2.blockedUsers && user2.blockedUsers.includes(user1.id)) {
    return { compatible: false, reason: 'blocked_by_user' };
  }

  return { compatible: true };
};

export const calculateCompatibilityScore = (user1, user2) => {
  let score = 50; // Base score

  // نفس المدينة
  if (user1.city === user2.city) score += 15;
  
  // نفس المنطقة
  if (user1.region === user2.region) score += 10;
  
  // تشابه الالتزام الديني
  if (user1.religious && user2.religious) {
    const diff = Math.abs(parseInt(user1.religious) - parseInt(user2.religious));
    if (diff === 0) score += 15;
    else if (diff === 1) score += 10;
    else if (diff === 2) score += 5;
  }
  
  // هوايات مشتركة
  if (user1.funActivities && user2.funActivities) {
    const common = user1.funActivities.filter(h => user2.funActivities.includes(h));
    score += Math.min(common.length * 5, 20);
  }

  // تشابه موعد الزواج
  if (user1.marriageTimeline === user2.marriageTimeline) score += 10;

  // تشابه الأمور غير القابلة للتنازل
  if (user1.dealbreakers && user2.dealbreakers) {
    const common = user1.dealbreakers.filter(d => user2.dealbreakers.includes(d));
    score += Math.min(common.length * 5, 15);
  }

  return Math.min(score, 100);
};

// ==================== Auth Error Messages ====================

export const getAuthErrorMessage = (code) => {
  const messages = {
    'auth/email-already-in-use': 'هذا الإيميل مسجل مسبقاً',
    'auth/invalid-email': 'البريد الإلكتروني غير صحيح',
    'auth/user-disabled': 'هذا الحساب معطل',
    'auth/user-not-found': 'كلمة المرور أو البريد الإلكتروني خطأ',
    'auth/wrong-password': 'كلمة المرور أو البريد الإلكتروني خطأ',
    'auth/invalid-credential': 'كلمة المرور أو البريد الإلكتروني خطأ',
    'auth/too-many-requests': 'محاولات كثيرة، حاول لاحقاً',
    'auth/weak-password': 'كلمة المرور ضعيفة',
    'auth/network-request-failed': 'تحقق من اتصالك بالإنترنت',
    'auth/requires-recent-login': 'انتهت الجلسة، سجل دخول مرة أخرى',
  };
  return messages[code] || 'حدث خطأ، حاول مرة أخرى';
};

// ==================== Security Logging ====================

export const logSecurityEvent = (event, details = {}) => {
  const log = {
    type: '[SECURITY]',
    event,
    timestamp: new Date().toISOString(),
    ...details
  };
  console.log(log);
};

// ==================== Content Moderation ====================

const BLOCKED_PATTERNS = [
  /\b\d{10}\b/, // Phone numbers
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/, // Phone patterns
  /@\w+/, // Social media handles
  /https?:\/\/\S+/, // URLs
  /wa\.me/i, // WhatsApp links
  /t\.me/i, // Telegram links
];

export const containsBlockedContent = (text) => {
  if (!text) return false;
  return BLOCKED_PATTERNS.some(pattern => pattern.test(text));
};

export const checkMessageSafety = (message, chatDay) => {
  // في أول 3 أيام، نمنع مشاركة معلومات التواصل
  if (chatDay <= 3 && containsBlockedContent(message)) {
    return {
      safe: false,
      reason: 'contact_info_too_early',
      message: 'لا يمكنك مشاركة معلومات التواصل في أول 3 أيام'
    };
  }
  return { safe: true };
};
